import React from "react";
import { ExternalLink, X } from "lucide-react";
import type { ProductPriceRecommendationDetail } from "../utils/helpers/productPricing";

type PricingRecommendationDetailModalProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
};

export const PricingRecommendationDetailModal: React.FC<PricingRecommendationDetailModalProps> = ({
  open,
  onClose,
  title,
  children,
}) => {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center p-6"
      data-testid="pricing-recommendation-modal"
    >
      <div className="fixed inset-0 z-40 bg-white bg-opacity-90" onClick={onClose} aria-hidden="true" />
      <dialog
        className="relative z-50 flex max-h-[95vh] w-full max-w-lg flex-col overflow-hidden rounded-xl bg-[#fff] shadow-[#00000029] shadow-[0_0px_8px_rgba(0,0,0,0.16)] md:max-w-2xl"
        open={open}
        onClose={onClose}
        style={{ margin: 0 }}
      >
        <div className="flex-1 overflow-y-auto p-4">
          <button
            type="button"
            onClick={onClose}
            className="ml-auto flex h-9 w-9 items-center justify-center rounded-full border border-[#e6e6e6] text-primary/50 hover:bg-surface-muted"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="mx-auto w-11/12">
            <div className="inventory-header-divider">
              <p className="inventory-header-title">{title}</p>
              <p className="inventory-header-subtitle">
                Market guidance to help you price with confidence
              </p>
            </div>
            <div className="space-y-4">{children}</div>
            <div className="inventory-form-footer">
              <button type="button" className="inventory-btn-primary w-full flex-none" onClick={onClose}>
                Got it
              </button>
            </div>
          </div>
        </div>
      </dialog>
    </div>
  );
};

export const ProductPriceRecommendationDetailView: React.FC<{
  detail: ProductPriceRecommendationDetail;
  displayUnit: string;
  itemName?: string;
}> = ({ detail, displayUnit, itemName }) => (
  <div className="space-y-4">
    <DetailMeta
      location={detail.locationLabel}
      method={detail.methodLabel}
      source={detail.dataSourceLabel}
      recommended={`$${detail.price.toFixed(2)}/${displayUnit}`}
      productLabel={itemName?.trim() || undefined}
    />

    <GuidanceBlock summary={detail.guidanceSummary} />

    <MarketBenchmarkTable title="What similar businesses charge" rows={detail.marketRows} />

    <ArticleLinks title="Further reading" articles={detail.articles} />
  </div>
);

const DetailMeta = ({
  location,
  method,
  source,
  recommended,
  productLabel,
}: {
  location: string;
  method: string;
  source: string;
  recommended: string;
  productLabel?: string;
}) => (
  <div className="inventory-detail-meta">
    {productLabel && (
      <div className="flex items-center justify-between gap-3">
        <span className="inventory-detail-meta-label">Product</span>
        <span className="inventory-detail-meta-value">{productLabel}</span>
      </div>
    )}
    <div className="flex items-center justify-between gap-3">
      <span className="inventory-detail-meta-label">Location</span>
      <span className="inventory-detail-meta-value">{location}</span>
    </div>
    <div className="flex items-center justify-between gap-3">
      <span className="inventory-detail-meta-label">Method</span>
      <span className="inventory-detail-meta-value">{method}</span>
    </div>
    <div className="flex items-center justify-between gap-3">
      <span className="inventory-detail-meta-label">Data source</span>
      <span className="inventory-detail-meta-value text-right">{source}</span>
    </div>
    <div className="flex items-center justify-between gap-3 border-t border-inventory-border pt-2">
      <span className="inventory-detail-meta-label">Suggested starting point</span>
      <span className="inventory-detail-highlight">{recommended}</span>
    </div>
  </div>
);

const GuidanceBlock = ({ summary }: { summary: string }) => (
  <div className="inventory-detail-guidance">
    <p className="inventory-detail-section-title">How we guide you</p>
    <p className="inventory-detail-body">
      {summary} This is a starting suggestion - adjust up or down based on your costs, demand, and positioning.
    </p>
  </div>
);

const MarketBenchmarkTable = ({
  title,
  rows,
}: {
  title: string;
  rows: ProductPriceRecommendationDetail["marketRows"];
}) => (
  <div>
    <p className="inventory-detail-section-title mb-2">{title}</p>
    <div className="inventory-detail-table">
      <table className="w-full text-left">
        <thead className="inventory-detail-table-head">
          <tr>
            <th className="inventory-detail-table-th">Comparable offering</th>
            <th className="inventory-detail-table-th">Typical range</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="border-t border-inventory-border">
              <td className="px-3 py-2.5">
                <p className="font-open-sans text-sm text-inventory-primary">{row.label}</p>
                <p className="font-open-sans text-[11px] text-inventory-primary/45">{row.context}</p>
              </td>
              <td className="px-3 py-2.5 font-open-sans text-sm font-semibold text-inventory-primary">
                {row.range}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const ArticleLinks = ({
  title,
  articles,
}: {
  title: string;
  articles: ProductPriceRecommendationDetail["articles"];
}) => (
  <div>
    <p className="inventory-detail-section-title mb-2">{title}</p>
    <div className="space-y-2">
      {articles.map((article) => (
        <ArticleCard key={article.id} article={article} />
      ))}
    </div>
  </div>
);

const ArticleCard = ({
  article,
}: {
  article: ProductPriceRecommendationDetail["articles"][number];
}) => {
  const isExternal = article.url.startsWith("http");

  const content = (
    <>
      <div className="min-w-0 flex-1">
        <p className="font-open-sans text-sm font-semibold text-inventory-primary">{article.title}</p>
        <p className="mt-0.5 font-open-sans text-[11px] text-inventory-primary/45">{article.source}</p>
        <p className="mt-1 font-open-sans text-xs leading-relaxed text-inventory-primary/55">{article.description}</p>
      </div>
      <ExternalLink className="mt-0.5 h-4 w-4 shrink-0 text-inventory-primary/35" />
    </>
  );

  if (isExternal) {
    return (
      <a href={article.url} target="_blank" rel="noopener noreferrer" className="inventory-article-card">
        {content}
      </a>
    );
  }

  return (
    <a href={article.url} className="inventory-article-card">
      {content}
    </a>
  );
};
